import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import TvecLayout from '../../components/TvecLayout';
import AuthService from '../../services/authService';
import axiosInstance from '../../utils/axiosInstance';

const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const hostOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
};

const inputClass = (error) =>
  `w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-2 ${
    error ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-100' : 'border-slate-300 focus:border-sky-600 focus:ring-sky-100'
  }`;

function Field({ label, required, error, hint, wide, children }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <label className="block text-sm font-medium text-slate-800">
        {label}
        {required ? <span className="ml-0.5 text-rose-600">*</span> : null}
      </label>
      <div className="mt-1.5">{children}</div>
      {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

export default function TvecLinks() {
  const staff = AuthService.getUser()?.superAdmin?.staff || {};
  const myName = staff.name || AuthService.getUser()?.superAdmin?.name || '';
  const blank = useCallback(
    () => ({ title: '', url: '', description: '', sharedBy: myName, sharedDate: todayIso() }),
    [myName],
  );

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get('/admin/tvec-links')
      .then((res) => { if (!cancelled) setLinks(res?.data?.data ?? []); })
      .catch(() => { if (!cancelled) setLoadError('Failed to load links.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return links;
    return links.filter((l) => [l.title, l.description, l.sharedBy, l.url].some((v) => (v || '').toLowerCase().includes(q)));
  }, [links, search]);

  const setField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(blank());
    setErrors({});
    setFormOpen(true);
  };

  const openEdit = (link) => {
    setEditingId(link.id);
    setForm({
      title: link.title,
      url: link.url,
      description: link.description || '',
      sharedBy: link.sharedBy || '',
      sharedDate: link.sharedDate || todayIso(),
    });
    setErrors({});
    setFormOpen(true);
    window.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setErrors({});
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    const next = {};
    if (!form.title.trim()) next.title = 'Title is required.';
    if (!form.url.trim()) next.url = 'Link is required.';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const res = await axiosInstance.put(`/admin/tvec-links/${editingId}`, form);
        setLinks((prev) => prev.map((l) => (l.id === editingId ? res.data.data : l)));
      } else {
        const res = await axiosInstance.post('/admin/tvec-links', form);
        setLinks((prev) => [res.data.data, ...prev]);
      }
      closeForm();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      else Swal.fire({ icon: 'error', title: 'Could not save', text: data?.message || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (link) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this link?',
      text: link.title,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/tvec-links/${link.id}`);
      setLinks((prev) => prev.filter((l) => l.id !== link.id));
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not delete', text: err?.response?.data?.message || 'Please try again.' });
    }
  };

  // The Excel sheet is built from whatever the search is showing.
  const exportExcel = () => {
    const rows = [
      ['S/N', 'Title', 'Link', 'Description', 'Shared By', 'Date'],
      ...visible.map((l, i) => [i + 1, l.title, l.url, l.description || '', l.sharedBy || '', l.sharedDate || '']),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 6 }, { wch: 36 }, { wch: 50 }, { wch: 50 }, { wch: 22 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TVEC Links');
    XLSX.writeFile(wb, `TVEC_Links_${todayIso()}.xlsx`);
  };

  return (
    <TvecLayout title="Links">
      <div className="w-full space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">TVEC links ({links.length})</h2>
            <p className="text-sm text-slate-500">Save useful links with a title, description, who shared them and when.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={exportExcel}
              disabled={visible.length === 0}
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Export Excel
            </button>
            <button
              type="button"
              onClick={openAdd}
              className="rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sky-700"
            >
              + Add link
            </button>
          </div>
        </div>

        {formOpen ? (
          <form onSubmit={save} noValidate className="overflow-hidden rounded-2xl border border-sky-200 bg-white shadow-sm">
            <header className="border-b border-slate-100 bg-sky-50/60 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-900">{editingId ? 'Edit link' : 'Add a link'}</h3>
            </header>
            <div className="grid grid-cols-1 gap-x-5 gap-y-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Title" required wide error={errors.title}>
                <input type="text" value={form.title} onChange={setField('title')} maxLength={200} placeholder="e.g. TVEC registration portal" className={inputClass(errors.title)} />
              </Field>
              <Field label="Link" required wide error={errors.url} hint="Paste the full address, e.g. https://www.tvec.gov.lk">
                <input type="text" inputMode="url" value={form.url} onChange={setField('url')} maxLength={1000} placeholder="https://" className={inputClass(errors.url)} />
              </Field>
              <Field label="Description" wide error={errors.description}>
                <textarea value={form.description} onChange={setField('description')} rows={3} maxLength={2000} placeholder="What is this link for?" className={`${inputClass(errors.description)} resize-none`} />
              </Field>
              <Field label="Shared by" error={errors.sharedBy}>
                <input type="text" value={form.sharedBy} onChange={setField('sharedBy')} maxLength={150} className={inputClass(errors.sharedBy)} />
              </Field>
              <Field label="Date" error={errors.sharedDate}>
                <input type="date" value={form.sharedDate} onChange={setField('sharedDate')} className={inputClass(errors.sharedDate)} />
              </Field>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
              <button type="button" onClick={closeForm} className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="rounded-lg bg-sky-600 px-5 py-2 text-xs font-semibold text-white hover:bg-sky-700 disabled:bg-sky-400">
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Save link'}
              </button>
            </div>
          </form>
        ) : null}

        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, description, shared by…"
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 sm:w-96"
        />

        {loadError ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{loadError}</p> : null}

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-white" />)}
          </div>
        ) : visible.length === 0 && !loadError ? (
          <p className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center text-sm text-slate-500">
            {links.length === 0 ? 'No links saved yet. Use “Add link” to save the first one.' : 'No links match your search.'}
          </p>
        ) : (
          <div className="space-y-3">
          {/* Wide screens: full-width table */}
          <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:block">
            <table className="w-full table-fixed text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-12 px-4 py-3 font-semibold">#</th>
                  <th className="w-[26%] px-4 py-3 font-semibold">Title / Link</th>
                  <th className="px-4 py-3 font-semibold">Description</th>
                  <th className="w-44 px-4 py-3 font-semibold">Shared by</th>
                  <th className="w-32 px-4 py-3 font-semibold">Date</th>
                  <th className="w-56 px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((link, i) => (
                  <tr key={link.id} className="align-top transition hover:bg-sky-50/50">
                    <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                    <td className="px-4 py-3">
                      <a href={link.url} target="_blank" rel="noreferrer noopener" className="block break-words font-semibold text-sky-700 hover:underline">
                        {link.title}
                      </a>
                      <p className="truncate text-xs text-slate-500">{hostOf(link.url)}</p>
                    </td>
                    <td className="whitespace-pre-line break-words px-4 py-3 text-slate-600">{link.description || '—'}</td>
                    <td className="break-words px-4 py-3 text-slate-800">{link.sharedBy || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-800">{formatDate(link.sharedDate)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <a href={link.url} target="_blank" rel="noreferrer noopener" className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white no-underline hover:bg-sky-700">
                          Open
                        </a>
                        <button type="button" onClick={() => openEdit(link)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">
                          Edit
                        </button>
                        <button type="button" onClick={() => remove(link)} className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Phones: cards */}
          <ul className="space-y-3 md:hidden">
            {visible.map((link) => (
              <li key={link.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <a href={link.url} target="_blank" rel="noreferrer noopener" className="block break-words text-sm font-semibold text-sky-700 hover:underline sm:text-base">
                      {link.title}
                    </a>
                    <p className="truncate text-xs text-slate-500">{hostOf(link.url)}</p>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <a href={link.url} target="_blank" rel="noreferrer noopener" className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white no-underline hover:bg-sky-700">
                      Open
                    </a>
                    <button type="button" onClick={() => openEdit(link)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">
                      Edit
                    </button>
                    <button type="button" onClick={() => remove(link)} className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50">
                      Delete
                    </button>
                  </div>
                </div>
                {link.description ? <p className="mt-2 whitespace-pre-line text-sm text-slate-600">{link.description}</p> : null}
                <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span>Shared by <span className="font-semibold text-slate-700">{link.sharedBy || '—'}</span></span>
                  <span>Date <span className="font-semibold text-slate-700">{formatDate(link.sharedDate)}</span></span>
                </p>
              </li>
            ))}
          </ul>
          </div>
        )}
      </div>
    </TvecLayout>
  );
}
